import { useState } from 'react';

import { AgentsList } from '../../components/agents/AgentsList';
import { ScenarioEditor } from '../../components/agents/ScenarioEditor';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { useToast } from '../../components/ui/toast';
import { useAgentList, useCreateAgent, usePatchAgent } from '../../hooks/use-agents';
import { useCreateScenario, useScenarioList } from '../../hooks/use-scenarios';

export function AgentsPage() {
  const { data: agents, isLoading, isError } = useAgentList();
  const { data: scenarios } = useScenarioList();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newAgentName, setNewAgentName] = useState('');
  const createAgent = useCreateAgent();
  const createScenario = useCreateScenario();
  const patchAgent = usePatchAgent();
  const toast = useToast();

  const selectedAgent = agents?.find((a) => a.id === selectedId) ?? null;
  const linkedScenario = scenarios?.find((s) => s.id === selectedAgent?.scenario_id) ?? null;

  function submitCreateAgent(e: React.FormEvent) {
    e.preventDefault();
    if (!newAgentName.trim()) return;
    createAgent.mutate(
      { name: newAgentName.trim() },
      {
        onSuccess: () => setNewAgentName(''),
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось создать агента'),
      },
    );
  }

  function createScenarioForAgent() {
    if (!selectedAgent) return;
    createScenario.mutate(`Сценарий для ${selectedAgent.name}`, {
      onSuccess: (res) => {
        patchAgent.mutate({ id: selectedAgent.id, body: { scenario_id: res.id } });
      },
      onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось создать сценарий'),
    });
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-4 py-3">
        <h1 className="text-lg font-semibold">Агенты</h1>
      </div>
      <div className="flex min-h-0 flex-1">
        <div className="w-80 shrink-0 overflow-y-auto border-r border-border">
          <form onSubmit={submitCreateAgent} className="flex gap-1 border-b border-border p-2">
            <Input
              placeholder="Имя нового агента"
              value={newAgentName}
              onChange={(e) => setNewAgentName(e.target.value)}
              aria-label="Имя нового агента"
            />
            <Button type="submit" size="sm" disabled={!newAgentName.trim() || createAgent.isPending}>
              +
            </Button>
          </form>
          {isLoading && (
            <div className="space-y-2 p-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          )}
          {isError && <div className="p-4 text-sm text-red-600">Не удалось загрузить агентов.</div>}
          {!isLoading && !isError && (
            <AgentsList agents={agents ?? []} selectedId={selectedId} onSelect={setSelectedId} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {!selectedAgent && (
            <div className="flex h-full items-center justify-center text-sm text-muted-fg">
              Выберите агента, чтобы настроить его сценарий
            </div>
          )}
          {selectedAgent && !linkedScenario && (
            <div className="flex h-full flex-col items-center justify-center gap-3">
              <p className="text-sm text-muted-fg">У агента «{selectedAgent.name}» ещё нет сценария</p>
              <Button onClick={createScenarioForAgent} disabled={createScenario.isPending}>
                Создать сценарий
              </Button>
            </div>
          )}
          {selectedAgent && linkedScenario && <ScenarioEditor scenario={linkedScenario} />}
        </div>
      </div>
    </div>
  );
}
